<template>
  <Teleport to="body" :disabled="!toBody">
    <div
      :class="[
        'vfm vfm--inset vfm--touch-none popup-scroll',
        isFixed ? 'vfm--fixed' : '',
        vfmCls,
      ]"
      :style="{
        visibility: dialogVisible ? 'visible' : 'hidden',
        zIndex: newZIndex,
      }"
    >
      <div
        :class="[
          'dialogContentContainer',
          mask
            ? `vfm__overlay vfm--overlay vfm--absolute vfm--inset ${maskCls}`
            : '',
        ]"
        @click="
          () => {
            if (mask && maskClosable) {
              handleClose();
            }
          }
        "
      >
        <div
          :class="[
            'vfm__container vfm--absolute vfm--inset vfm--outline-none',
            !isMobile
              ? 'flex items-center justify-center'
              : 'flex flex-wrap content-end',
            containerCls,
          ]"
        >
          <div
            :class="[
              'vfm__content',
              width,
              height,
              customCls,
              animation,
              contentBg,
            ]"
            @click.stop
          >
            <div
              v-if="isHeader && !isBack"
              :class="[
                'relative flex justify-end items-center h-[54px] px-[12px] shrink-0',
                headerCls,
              ]"
            >
              <!-- 标题相对整个 header 居中：原先是 flex 子项，右边有关闭键左边没占位，被挤得往左偏。
                   这里用 left-1/2 + translate 让标题以 header 正中为基准，不受两侧留白影响；
                   max-w 给按钮让位，长标题才截断 -->
              <p
                class="absolute left-1/2 -translate-x-1/2 max-w-[calc(100%-88px)] text-center text-[16px] text-e-g-color-14 capitalize font-bold truncate"
                :class="[titleCls]"
              >
                {{ title }}
              </p>

              <div class="relative z-[1] flex items-center justify-end space-x-[6px]">
                <slot name="closeBefore"></slot>
                <button
                  v-if="isClose"
                  class="flex justify-center items-center shrink-0 w-[28px] h-[28px] rounded-[6px] bg-e-g-color-109 text-e-g-color-14"
                  @click="handleClose"
                >
                  <i
                    class="inline-block h-[max-content] w-[max-content] icon sysicon-n-clean-1 cursor-pointer text-[18px] font-bold"
                  ></i>
                </button>
              </div>
            </div>

            <!--mobile header-->
            <div
              v-if="isHeader && isBack"
              :class="[
                'relative flex justify-between items-center h-[48px] px-[12px] shrink-0 text-e-g-color-14',
                headerCls,
              ]"
            >
              <button
                v-if="isClose"
                class="relative z-[1] flex justify-center items-center shrink-0 w-[28px] h-[28px] text-[12px] rounded-[6px] bg-e-g-color-109 text-e-g-color-14"
                @click="handleClose"
              >
                <i
                  class="inline-block h-[max-content] w-[max-content] text-[12px] icon sysicon-n-back"
                ></i>
              </button>
              <!-- 标题要相对整个 header 居中：原先是 flex 子项，被左侧返回键挤得往右偏。
                   用 left-1/2 + translate 以 header 正中为基准，不受两侧留白影响 -->
              <div
                class="absolute left-1/2 -translate-x-1/2 max-w-[calc(100%-88px)] text-[16px] leading-[1.5] font-bold truncate text-center"
              >
                {{ title }}
              </div>
              <slot name="headerAfter"></slot>
            </div>

            <div :class="['min-h-0 overflow-y-auto touch-pan-y', contentCls]">
              <slot></slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import "./index.css";

// 维护本地 device 状态，因为通过 createVNode 动态创建的组件无法使用 inject
const device = ref(window.innerWidth < 640 ? "mobile" : "desktop");

// 使用 matchMedia 监听媒体查询变化，能更好地响应浏览器开发者工具的设备模式切换
const updateDevice = () => {
  const windowWidth = window.innerWidth;
  device.value = windowWidth < 640 ? "mobile" : "desktop";
};

// 监听媒体查询变化（包括浏览器开发者工具的设备模式切换）
const handleMediaChange = (e) => {
  device.value = e.matches ? "mobile" : "desktop";
};

let mediaQuery = null;
// 将当前实例的关闭方法注册到全局注册表，便于在 Android 侧滑/后退等场景一键关闭所有弹框
let __removeFromGlobalDialogRegistry = null;

onMounted(() => {
  // 使用 matchMedia 监听 (max-width: 639px) 的变化
  mediaQuery = window.matchMedia("(max-width: 639px)");

  // 使用 addEventListener 而不是 addListener（更现代的方式）
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener("change", handleMediaChange);
  } else {
    // 兼容旧浏览器
    mediaQuery.addListener(handleMediaChange);
  }

  // 同时监听 resize 事件作为备用
  window.addEventListener("resize", updateDevice);

  // 初始化时检查一次
  updateDevice();

  try {
    if (!window.__VUE_DIALOG_CLOSE_REGISTRY) {
      window.__VUE_DIALOG_CLOSE_REGISTRY = [];

      // 暴露一个全局方法用于关闭所有注册的弹框
      window.closeAllDialogs = () => {
        try {
          window.__VUE_DIALOG_CLOSE_REGISTRY.forEach((fn) => {
            try {
              if (typeof fn === "function") fn();
            } catch (e) {
              // 忽略单个弹框关闭错误，继续关闭其他弹框
            }
          });
        } finally {
          // 关闭后清空注册表，避免重复调用影响已卸载实例
          window.__VUE_DIALOG_CLOSE_REGISTRY = [];
        }
      };
    }

    // 注册当前实例的 handleClose
    window.__VUE_DIALOG_CLOSE_REGISTRY.push({
      close: handleClose,
      key: props.hash ? props.hash : props.title,
    });

    // 提供一个移除函数，在卸载时调用
    __removeFromGlobalDialogRegistry = () => {
      const idx = window.__VUE_DIALOG_CLOSE_REGISTRY.findIndex(
        (item) => item.close === handleClose
      );
      if (idx > -1) window.__VUE_DIALOG_CLOSE_REGISTRY.splice(idx, 1).close;
    };
  } catch (e) {
    // 安全降级：若 window 不可用或其他异常，忽略
  }
});

onUnmounted(() => {
  if (mediaQuery) {
    if (mediaQuery.removeEventListener) {
      mediaQuery.removeEventListener("change", handleMediaChange);
    } else {
      // 兼容旧浏览器
      mediaQuery.removeListener(handleMediaChange);
    }
  }
  window.removeEventListener("resize", updateDevice);

  try {
    if (__removeFromGlobalDialogRegistry) __removeFromGlobalDialogRegistry();
  } catch (e) {}
});

const isMobile = computed(() => device.value === "mobile");

// Props定义
const props = defineProps({
  customCls: {
    type: String,
    default: "",
  },
  visible: {
    type: Boolean,
    default: false,
  },
  close: {
    type: Function,
    default: () => {},
  },
  animationName: {
    type: String,
    default: "",
  },
  isClose: {
    type: Boolean,
    default: true,
  },
  width: {
    type: String,
    default: "",
  },
  height: {
    type: String,
    default: "",
  },
  title: {
    type: String,
    default: "Prompt",
  },
  titleCls: {
    type: String,
    default: "",
  },
  isHeader: {
    type: Boolean,
    default: true,
  },
  headerCls: {
    type: String,
    default: "",
  },
  contentBg: {
    type: String,
    default: "",
  },
  zIndex: {
    type: Number,
    default: 0,
  },
  containerCls: {
    type: String,
    default: "",
  },
  vfmCls: {
    default: null,
  },
  mask: {
    type: Boolean,
    default: true,
  },
  maskClosable: {
    type: Boolean,
    default: true,
  },
  maskCls: {
    default: null,
  },
  toBody: {
    type: Boolean,
    default: true,
  },
  isFixed: {
    type: Boolean,
    default: true,
  },
  contentCls: {
    type: String,
    default: "",
  },
  isBack: {
    type: Boolean,
    default: false,
  },
  hash: {
    type: String,
    default: "",
  },
});

// 声明自定义事件
const emit = defineEmits(["close"]);

// 全局 z-index 管理
let globalZIndex = 500;
const getNextZIndex = () => ++globalZIndex;

// 响应式状态
const dialogVisible = ref(props.visible);
const isAnimated = ref(false);
const newZIndex = ref(props?.zIndex ?? globalZIndex);

// 监听 visible 变化
watch(
  () => props.visible,
  (newVal) => {
    dialogVisible.value = newVal;
  }
);

// 处理 HTML overflow 逻辑
watch(
  dialogVisible,
  (newVal) => {
    const body = document.querySelector("html");
    if (!body) return;

    // 只处理显示时的逻辑，关闭时的逻辑移到handleClose中处理
    if (newVal) {
      // 当dialog显示时，隐藏html滚动条
      body.style.overflow = "hidden";
      // body.style.position = "fixed";

      // 更改 hash
      if (props.hash) window.location.hash = `#${props.hash}`;
    }
  },
  {
    immediate: true,
  }
);

// 处理 z-index
watch(
  [dialogVisible, () => props.zIndex],
  ([visible, zIndex]) => {
    if (visible) {
      if (zIndex) {
        newZIndex.value = zIndex;
      } else {
        const index = getNextZIndex();
        newZIndex.value = index;
      }
    }
  },
  {
    immediate: true,
  }
);

// 关闭处理
const handleClose = (e, isBack) => {
  isAnimated.value = true;

  setTimeout(() => {
    emit("close", false);
    dialogVisible.value = false;
    isAnimated.value = false;

    // 处理 hash 回退
    if (props.hash && !isBack) {
      window.__SKIP_BEFORE_EACH = true;
      history.back();
    }

    // 直接在handleClose中处理滚动条恢复逻辑
    setTimeout(() => {
      const body = document.querySelector("html");
      if (body) {
        // 检查页面上是否还有其他可见的dialog
        const visibleDialogs = document.querySelectorAll(
          '.vfm:not([style*="visibility: hidden"])'
        );

        // 可见dialog数量判断：需要大于1时才不恢复滚动，因为当前dialog可能还在DOM中
        // 但实际上由于我们设置了visibility: hidden，所以如果没有其他dialog，这里的数量会是0或1（当前关闭中的dialog）
        if (visibleDialogs.length <= 0) {
          body.style.overflow = "auto";
          body.style.position = "inherit";
        }
      }
    }, 50); // 短延迟确保DOM更新
  }, 300);
};

// 动画类型
const animationType = computed(() => {
  if (props.animationName) {
    return props.animationName;
  } else {
    return isMobile.value ? "slideBottom" : "zoom";
  }
});

// 动画类名
const animation = computed(() => {
  if (isAnimated.value) {
    return `${animationType.value}-out`;
  }
  return `${animationType.value}-in`;
});

// 暴露方法给父组件
defineExpose({
  handleClose,
});
</script>
